/**
 * ## Ours (Base UI)
 * Animated, supports nested dialogs via CSS vars. Backdrop + Viewport + Popup are separate exports
 * but also collapsed into `DialogPopup` (Coss UI decision, same as `PopoverPopup`).
 * * `DialogClose` — close button (add manually; not built into `DialogPopup`)
 * * `DialogBackdrop` / `DialogOverlay` — blurred backdrop
 * * `DialogViewport` — full-screen grid container
 * * `DialogPopup` / `DialogContent` — collapses Portal + Backdrop + Viewport + Popup; nested-dialog scale/opacity handled automatically
 * * `DialogPanel` — scrollable body (`ScrollArea`); `scrollFade?: boolean`
 * * `DialogCreateHandle` — `DialogPrimitive.createHandle` re-export
 * * We also don't render buttons like "Cancel"
 *
 * ## Shadcn UI
 * Basic dialog. `DialogContent` has a built-in X close button and MFE auto-focus fix applied.
 * * `DialogClose` — close button
 * * `DialogOverlay` — semi-transparent overlay (no blur)
 * * `DialogContent` — collapses Portal + Overlay + Content + X close button
 */
/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */

'use client'

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { ScrollArea } from './scroll-area'
import { cn } from '../lib/utils'

const DialogCreateHandle = DialogPrimitive.createHandle
const Dialog = DialogPrimitive.Root
const DialogPortal = DialogPrimitive.Portal

function DialogTrigger(props: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogClose(props: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogBackdrop({
  className,
  ...props
}: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      className={cn(
        'fixed inset-0 z-50 backdrop-blur-md transition-[opacity,backdrop-filter] duration-350 ease-out data-ending-style:opacity-0 data-starting-style:opacity-0',
        className,
      )}
      data-slot="dialog-backdrop"
      {...props}
    />
  )
}

function DialogViewport({
  className,
  ...props
}: DialogPrimitive.Viewport.Props) {
  return (
    <DialogPrimitive.Viewport
      className={cn(
        'fixed inset-0 z-50 grid grid-rows-[1fr_auto_1fr] justify-items-center',
        className,
      )}
      data-slot="dialog-viewport"
      {...props}
    />
  )
}

function DialogPopup({
  portalContainer,
  className,
  children,
  ...props
}: DialogPrimitive.Popup.Props & {
  portalContainer?: DialogPrimitive.Portal.Props['container']
}) {
  return (
    <DialogPortal container={portalContainer}>
      <DialogBackdrop />
      <DialogViewport>
        <DialogPrimitive.Popup
          className={cn(
            'bg-surface-2 text-on-surface relative row-start-2 flex max-h-full min-h-0 w-full max-w-lg -translate-y-[calc(1.25rem*var(--nested-dialogs))] scale-[calc(1-0.1*var(--nested-dialogs))] flex-col rounded-[28px] opacity-[calc(1-0.1*var(--nested-dialogs))] transition-[scale,opacity,translate] duration-350 ease-out will-change-transform not-dark:bg-clip-padding data-ending-style:scale-98 data-ending-style:opacity-0 data-nested:data-ending-style:translate-y-8 data-nested-dialog-open:origin-top data-starting-style:scale-98 data-starting-style:opacity-0 data-nested:data-starting-style:translate-y-8',
            className,
          )}
          data-slot="dialog-popup"
          {...props}
        >
          {children}
        </DialogPrimitive.Popup>
      </DialogViewport>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col gap-2 p-6 in-[[data-slot=dialog-popup]:has([data-slot=dialog-panel])]:pb-3',
        className,
      )}
      data-slot="dialog-header"
      {...props}
    />
  )
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 px-6 pt-4 pb-6 in-[[data-slot=dialog-popup]:has([data-slot=dialog-panel])]:pt-3 sm:flex-row sm:justify-end sm:rounded-b-[calc(var(--radius-2xl)-1px)]',
        className,
      )}
      data-slot="dialog-footer"
      {...props}
    />
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      className={cn(
        'font-heading text-xl leading-none font-semibold',
        className,
      )}
      data-slot="dialog-title"
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      className={cn('text-on-surface-secondary text-sm', className)}
      data-slot="dialog-description"
      {...props}
    />
  )
}

function DialogPanel({
  className,
  scrollFade = true,
  ...props
}: React.ComponentProps<'div'> & { scrollFade?: boolean }) {
  return (
    <ScrollArea scrollFade={scrollFade}>
      <div
        className={cn(
          'p-6 in-[[data-slot=dialog-popup]:has([data-slot=dialog-footer]:not(.border-t))]:pb-1 in-[[data-slot=dialog-popup]:has([data-slot=dialog-header])]:pt-1',
          className,
        )}
        data-slot="dialog-panel"
        {...props}
      />
    </ScrollArea>
  )
}

export {
  DialogCreateHandle,
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogClose,
  DialogBackdrop,
  DialogBackdrop as DialogOverlay,
  DialogPopup,
  DialogPopup as DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogPanel,
  DialogViewport,
}
